import { ensureFreshAccess, getTokens } from '../utils/authFetch';

const EVA_PROXY_PREFIX = '/eva';

async function readResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const detail = typeof body === 'string'
      ? body
      : body?.error || body?.detail || `HTTP ${response.status}`;
    const error = new Error(detail || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return body;
}

export async function evaFetch(path, options = {}, retryAfterRefresh = true) {
  const { access } = getTokens();
  if (!access) {
    throw new Error('Сессия завершена. Выполните вход повторно.');
  }

  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${access}`);
  headers.set('Accept', 'application/json, text/plain;q=0.9');

  const response = await fetch(`${EVA_PROXY_PREFIX}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401 && retryAfterRefresh) {
    await ensureFreshAccess();
    return evaFetch(path, options, false);
  }

  return response;
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await evaFetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return readResponse(response);
}

export const evaApi = {
  health: () => request('/health'),
  documents: (limit = 12) => request(`/admin/documents?limit=${limit}`),
  document: (documentId) => request(`/admin/document/${encodeURIComponent(documentId)}`),
  documentHtml: (documentId) => request(`/admin/document/${encodeURIComponent(documentId)}/html`),
  uploadKnowledgeMedia: (payload) => request('/admin/knowledge/media', { method: 'POST', body: payload }),
  createDocument: (payload) => request('/admin/document/create', {
    method: 'POST',
    body: payload,
  }),
  updateDocument: (documentId, payload) => request(`/admin/document/${encodeURIComponent(documentId)}`, {
    method: 'POST',
    body: payload,
  }),
  deleteDocuments: (documentIds) => request('/admin/documents/delete', {
    method: 'POST',
    body: { document_ids: documentIds },
  }),
  prompts: () => request('/admin/prompts'),
  loadScene: (sceneName) => request(`/scene/load?scene_name=${encodeURIComponent(sceneName)}`),
  saveScene: (sceneName, blocks, connections, hiddenDialogIds = []) => request('/scene/save', {
    method: 'POST',
    body: {
      scene_name: sceneName,
      blocks,
      connections,
      hidden_dialog_ids: hiddenDialogIds,
    },
  }),
  updatePrompt: (promptId, payload) => request(`/admin/prompt/${encodeURIComponent(promptId)}`, {
    method: 'POST',
    body: payload,
  }),
  createPrompt: (payload) => request('/admin/prompt', {
    method: 'POST',
    body: payload,
  }),
  deletePrompt: (promptId) => request('/admin/prompt/delete', {
    method: 'POST',
    body: { id: promptId },
  }),
  activatePrompt: (promptId) => request('/admin/prompt/activate', {
    method: 'POST',
    body: { id: promptId },
  }),
  trainingStatus: () => request('/admin/training/status'),
  trainingPreflight: () => request('/admin/training/preflight'),
  startTraining: () => request('/admin/training/start', { method: 'POST', body: {} }),
  runCommand: (command, params = {}) => request('/admin/command', {
    method: 'POST',
    body: { command, params },
  }),
};
